import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useAuth } from './auth';

/**
 * A list's filters, kept for whoever set them until they clear them themselves (operator,
 * 2026-10-22: "when i back the filter cleared i want to keep my filter until i clear it my
 * self"). Opening a task and coming back, the menu, a reload or the next day all find the list
 * as it was left. Kept in this browser, per person, so someone else signing in here starts clean.
 */
export function useKept<T>(
  name: string,
  fallback: T,
  given?: T,
  isBlank: (value: T) => boolean = (value) => JSON.stringify(value) === JSON.stringify(fallback),
): [T, Dispatch<SetStateAction<T>>] {
  const { user } = useAuth();
  const key = `viganium.kept.${user?.id ?? 'anyone'}.${name}`;
  // A link that says what to show (the dashboard's urgent tasks) wins over what was kept.
  const [value, setValue] = useState<T>(() => given ?? read<T>(key) ?? fallback);

  useEffect(() => {
    try {
      if (isBlank(value)) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage refused (a private window, a full quota): the list still works, it just forgets.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);

  return [value, setValue];
}

/** The filters themselves: chosen values by name, `''` for "all". */
export function useKeptFilters(list: string, given?: Record<string, string>) {
  return useKept<Record<string, string>>(`${list}.filters`, {}, given, (filters) => !anyFilter(filters));
}

/** Whether any filter is set — what shows the Clear button. */
export function anyFilter(filters: Record<string, string>) {
  return Object.values(filters).some(Boolean);
}

function read<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}
